import {RefObject, useCallback, useEffect, useState} from 'react';

import {getReactRootDocument} from './dom';
import {parentTreeHasDataAttribute} from './functions';

const isChildElement = (element: HTMLElement, parent: HTMLElement): boolean => {
    if (element === parent) {
        return true;
    }

    // while element has parent, check if the parent is the target parent
    while (element.parentElement) {
        element = element.parentElement;

        if (element === parent) {
            return true;
        }
    }
    return false;
};

type ModalOutsideClickProps = {
    enabled?: boolean;
    exclude?: RefObject<HTMLElement>[];
    isOpen?: boolean;
    onClose: () => void;
};
export const useModalMouseEscape = ({
    enabled,
    exclude = [],
    isOpen,
    onClose,
}: ModalOutsideClickProps): void => {
    const onMouseDown = useCallback(
        (event: MouseEvent) => {
            const targetElement = event.composedPath()[0] as HTMLElement;

            const clickedInsideExludedElements = exclude.some((ref) => {
                if (ref.current) {
                    return isChildElement(targetElement, ref.current);
                }
                return false;
            });

            if (clickedInsideExludedElements) {
                return;
            }

            if (!parentTreeHasDataAttribute(targetElement, 'modal-safe')) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen && enabled) {
            document.addEventListener('mousedown', onMouseDown);
        }

        return () => {
            document.removeEventListener('mousedown', onMouseDown);
        };
    }, [isOpen, onMouseDown, enabled]);
};

/**
 * Returns `true` if the currently focused element is a interactive element such as textinput, textarea, button, etc.
 */
const isFocusedInteractiveElement = (): boolean => {
    const {activeElement} = document;

    if (!activeElement) {
        return false;
    }

    const interactiveElements = ['input', 'textarea', 'button'];

    return interactiveElements.includes(activeElement.tagName.toLowerCase());
};
type ModalKeyEscapeProps = {
    hasCancel?: boolean;
    isOpen?: boolean;
    onClose: () => void;
    submitCount: number;
};
export const useModalKeyEscape = ({
    hasCancel,
    isOpen,
    onClose,
    submitCount,
}: ModalKeyEscapeProps): void => {
    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (hasCancel || submitCount === 0) {
                    onClose();
                }

                return;
            }

            if (
                !isFocusedInteractiveElement() && // If the user is focused on an interactive element, let the browser handle the event
                event.key === 'Enter' && // If there is no submit, there must be a cancel so cancel
                submitCount === 0
            ) {
                onClose();
            }
        },
        [hasCancel, onClose, submitCount],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', onKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen, onKeyDown]);
};

const DEFAULT_BUTTONS = {
    hasCancel: false,
    submitCount: 0,
};
type UseModalButtonsReturnType = {
    hasCancel: boolean;
    onCheckButtons: () => void;
    submitCount: number;
};
export const useModalButtons = (): UseModalButtonsReturnType => {
    const [{hasCancel, submitCount}, setButtons] = useState(
        () => DEFAULT_BUTTONS,
    );

    const onCheckButtons = useCallback(() => {
        const rootDocument = getReactRootDocument();
        const cancelButtons =
            rootDocument?.querySelectorAll(
                '[data-modal-button-type="cancel"]',
            ) || [];
        const submitButtons =
            rootDocument?.querySelectorAll(
                '[data-modal-button-type="submit"]',
            ) || [];

        if (cancelButtons.length > 0 || submitButtons.length > 0) {
            setButtons({
                hasCancel: cancelButtons.length > 0,
                submitCount: submitButtons.length,
            });
        }
    }, []);

    return {
        hasCancel,
        onCheckButtons,
        submitCount,
    };
};
